<template>
  <div class="ai-message-content">
    <div v-if="renderedHtml" v-html="renderedHtml" class="prose-content"></div>
    <div v-else-if="isStreaming" class="text-gray-400 text-sm">...</div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { parseMarkdown } from '../utils/markdown';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  isStreaming: {
    type: Boolean,
    default: false,
  },
  parsedHtml: {
    type: String,
    default: null,
  },
});

// parsedHtml（完了時にChatApp.vueから渡される）があればそれを使い、
// なければ content を毎回 parseMarkdown する（ストリーミング中も同じ経路）
const renderedHtml = computed(() => {
  if (props.parsedHtml) return props.parsedHtml;
  if (!props.content) return '';
  return parseMarkdown(props.content);
});
</script>

<style scoped>
.ai-message-content {
  line-height: 1.8;
  font-size: 14px;
  color: #374151;
}

.prose-content {
  max-width: 100%;
}

/* 見出し共通 */
.prose-content :deep(h1),
.prose-content :deep(h2),
.prose-content :deep(h3),
.prose-content :deep(h4),
.prose-content :deep(h5),
.prose-content :deep(h6) {
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
}

/* h1: 大見出し */
.prose-content :deep(h1) {
  font-size: 20px;
  margin-top: 1.5rem;
  margin-bottom: 0.6rem;
}

/* h2: セクション区切り（下線付き） */
.prose-content :deep(h2) {
  font-size: 18px;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid #e5e7eb;
}

/* h3: メインタイトル */
.prose-content :deep(h3) {
  font-size: 16px;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

/* h4: サブタイトル（左アクセントライン） */
.prose-content :deep(h4) {
  font-size: 14px;
  margin-top: 1rem;
  margin-bottom: 0.35rem;
  padding-left: 0.6rem;
  border-left: 3px solid #37acdb;
  color: #1f2937;
}

/* h5/h6: 小見出し */
.prose-content :deep(h5),
.prose-content :deep(h6) {
  font-size: 13px;
  margin-top: 0.75rem;
  margin-bottom: 0.3rem;
  color: #374151;
}

.prose-content :deep(h1:first-child),
.prose-content :deep(h2:first-child),
.prose-content :deep(h3:first-child),
.prose-content :deep(h4:first-child) {
  margin-top: 0;
}

/* 箇条書き */
.prose-content :deep(ul) {
  list-style-type: disc;
  margin: 0.4rem 0;
  padding-left: 1.5rem;
}

.prose-content :deep(ol) {
  list-style-type: decimal;
  margin: 0.4rem 0;
  padding-left: 1.75rem;
}

.prose-content :deep(li) {
  margin-bottom: 0.35rem;
  line-height: 1.75;
  display: list-item;
}

.prose-content :deep(li:last-child) {
  margin-bottom: 0;
}

/* 段落 */
.prose-content :deep(p) {
  margin: 0.5rem 0;
  line-height: 1.8;
}

.prose-content :deep(p:first-child) {
  margin-top: 0;
}

/* 先頭の余分な br を非表示 */
.prose-content :deep(br:first-child) {
  display: none;
}

/* 強調テキスト */
.prose-content :deep(strong) {
  font-weight: 700;
  color: #111827;
}

/* 水平線 */
.prose-content :deep(hr) {
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
}

/* コードブロック */
.prose-content :deep(pre) {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.prose-content :deep(code) {
  font-size: 13px;
}

.typing-cursor {
  color: #37acdb;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}
</style>
